import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, makeStyles, useTheme, List, ListItem, ListItemSecondaryAction, ListItemText, Typography } from '@material-ui/core/';
import { themeTypeToBool, setGradeColor } from "../../utils/helpers";
import { useTrail, animated } from 'react-spring';
import t from "../../languages/pl";
import PeriodSelect from './CountUpValue/PeriodSelect';
import { worstEco, topEco } from './../../utils/staticData';

const useStyles = makeStyles(theme => ({
    container: {
        minWidth: 350,
        margin: theme.spacing(1),
        background: theme.palette.background.paper,
        position: 'relative'
    },
    title: {
        width: "85%",
        margin: "auto",
        padding: 15,
        paddingBottom: 5
    },
    list: {
        width: "80%",
        margin: "auto",
        paddingBottom: 30
    },
    listItem: {
        border: "solid",
        borderWidth: "1px 0 1px 0",
        borderColor: "#707070",
    },
    listItemGrade: {
        fontWeight: 700,
        marginRight: 15
    },
    listHeader: {
        height: 35,
        fontWeight: 500,
        border: "solid",
        borderColor: "#707070",
        borderWidth: "0 0 1px 0",
        background: themeTypeToBool(theme.palette.type) ? "#F1F1F1" : "#43425D"
    },
    settingsIcon: {
        position: "absolute",
        top: 10,
        right: 10,
        padding: 0,
    },
    elementLabel: {
        cursor: 'pointer',
        "&:hover": {
            fontWeight: 600
        }
    }
}));

const setRowBackGround = (grade, opacity, themeType) => {
    if(grade > 0 && grade <= 2)
        return themeTypeToBool(themeType) ? `rgba(255, 88, 88, ${opacity})` : `rgba(216, 0, 0, ${opacity})`;
    else if(grade > 2 && grade <= 4)
        return `rgba(241, 149, 46, ${opacity})`;
    else if(grade > 4 && grade <= 6)
        return `rgba(234, 223, 43, ${opacity})`;
    else if(grade > 6 && grade <= 8)
        return `rgba(184, 255, 129, ${opacity})`;
    else if(grade > 8)
        return themeTypeToBool(themeType) ? `rgba(184, 255, 129, ${opacity})` : `rgba(89, 145, 46, ${opacity})`;
    else return themeTypeToBool(themeType) ? "#FFF" : "transparent";
};

const initialData = [];

const animationConfig = { mass: 5, tension: 2000, friction: 200, duration: 2000 };

const options = [
    {
        name: t.top,
        value: "top"
    },
    {
        name: t.worst,
        value: "worst"
    }
];

const best = "top";

function EcoTopList(props) {
    const theme = useTheme();
    const classes = useStyles(theme);
    const { title, data, attrName, headerLabel, onElementClick } = props;
    const [gradeData, setGradeData] = useState(initialData);
    const [periodValue, setPeriodValue] = useState(best);
    const trail = useTrail(gradeData.length, {
        config: animationConfig,
        opacity: 1,
        x: 0,
        height: 46,
        from: { opacity: 0, x: 20, height: 0 },
      });

    useEffect(() => {
        if(!data && Object.keys(data) !== [topEco, worstEco])
            return;
        if(periodValue === best)
            setGradeData(data[topEco]);
        else setGradeData(data[worstEco]);
    }, [data, periodValue]);

    const listOneRow = (data, theme, index) => {
        if(data != null && data[attrName] && data[attrName].length > 0){
            let backgroundColor = setRowBackGround(data.ocena, (1 - index / 7), theme.palette.type);
            return (
                <ListItem key={data[attrName]} className={classes.listItem} style={{ background: backgroundColor }}>
                    <ListItemText>
                        <Typography
                            variant="body1" className={classes.elementLabel}
                            onClick={() => onElementClick(data)}
                        >
                            {data[attrName]}
                        </Typography>
                    </ListItemText>
                    <ListItemSecondaryAction>
                        <Typography variant="h5" className={classes.listItemGrade} style={{ color: setGradeColor(data.ocena, theme.palette.type) }}>
                            {data.ocena}
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
            );
        }
    };

    return (
        <Paper className={classes.container} square>
            <Typography variant="subtitle2" className={classes.title}>
                {title}
            </Typography>
            <List className={classes.list}>
                <ListItem className={classes.listHeader}>
                    <ListItemText>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {headerLabel}
                        </Typography>
                    </ListItemText>
                    <ListItemSecondaryAction>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t.grade}
                        </Typography>
                    </ListItemSecondaryAction>
                </ListItem>
                {trail.map(({ x, height, ...rest }, index) => (
                    <animated.div
                        key={index}
                        className="trails-text"
                        style={{ ...rest, transform: x.interpolate(x => `translate3d(0,${x}px,0)`) }}
                        >
                        <animated.div style={{ height }}>{listOneRow(gradeData[index], theme, index)}</animated.div>
                    </animated.div>
                ))}
            </List>
            <PeriodSelect
                className={classes.settingsIcon}
                value={periodValue}
                onChange={(value) => setPeriodValue(value)}
                buttonOptions={options}
            />
        </Paper>
    );
}

export default EcoTopList;

EcoTopList.propTypes = {
    title: PropTypes.string,
    data: PropTypes.object,
    attrName: PropTypes.string.isRequired,
    headerLabel: PropTypes.string,
    onElementClick: PropTypes.func
};